<template>
  <div>
    <el-input class="card-info-input" :class="{ 'is-invalid': cardNumRequired || !validator.cardNum }"
      placeholder="1234 1234 1234 1234" maxlength="23" inputmode="numeric" v-model="formCardNum" @blur="cardNumberBlur"
      @focus="cardNumberFocus" @change="cardNumberChange">
      <div slot="suffix">
        <!-- 卡种滚动效果，没有值的时候默认显示 -->
        <div class="card-info-suffix" v-show="!formCardNum">
          <div class="card-info-suffix-content" v-if="cardTypeInfoFormatted?.length">
            <img v-for="(item) in cardTypeInfoFormatted.slice(0, 3)" :key="item.logoPath" :src="item.logoPath" alt=""
              class="fixed-item" />
            <div class="fade-carousel" v-show="cardTypeInfoFormatted.length > 3">
              <img v-for="(item, index) in cardTypeInfoFormatted.slice(3)" :key="item.logoPath" :src="item.logoPath"
                class="fade-item" :class="{ active: index === activeIndex }" alt="" />
            </div>
          </div>
        </div>

        <div class="card-info-suffix-card" v-show="formCardNum">
          <img :src="card.logoPath" alt="" class="fixed-item" />
        </div>
      </div>
    </el-input>
    <div class="card-info-under-group">
      <!-- 有效期 -->
      <el-input class="effectiveDate-input" :class="{ 'is-invalid': effectiveDateRequired || !validator.effectiveDate }"
        maxlength="7" placeholder="MM / YY" autocomplete="cc-exp" inputmode="numeric" v-model="formEffectiveDate"
        @input.native="effectiveDateChangeHandler" @blur="effectiveDateBlur" @focus="effectiveDateFocus"
        @change="effectiveDateChange">
      </el-input>

      <!-- cvv -->
      <el-input class="cvv-input" :class="{ 'is-invalid': cvvRequired || !validator.cvv }" maxlength="4"
        placeholder="CVC" autocomplete="cc-csc" v-model="formCvv" inputmode="numeric" @input.native="cvvChangeHandler"
        @blur="cvvBlur" @focus="cvvFocus" @change="cvvChange">
        <div slot="suffix">
          <div class="img-cvv-box">
            <img class="icon-cvv" src="@/assets/images/icon-cvv.png" />
          </div>
        </div>
      </el-input>

    </div>

    <transition name="el-zoom-in-top">
      <div v-if="errorMsg" class="error-msg">{{ errorMsg }}</div>
    </transition>
  </div>
</template>

<script>
import { getCard } from '@/utils/validator';

import { cardNumberRules, effectiveDateRules, cvvRules } from './rules';

export default {
  name: 'card-information',
  props: {
    rules: {
      type: Object,
      default: () => [],
    },
    cardNum: {
      type: String,
      default: '',
    },
    effectiveDate: {
      type: String,
      default: '',
    },
    cvv: {
      type: String,
      default: '',
    },
    cardTypeInfo: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeIndex: 0,
      card: { logoPath: require('@/assets/images/cards/Default.png') },
      formData: {
        cardNum: this.cardNum,
        effectiveDate: this.effectiveDate,
        cvv: this.cvv,
      },
      // 格式校验结果
      validator: {
        cardNum: true,
        effectiveDate: true,
        cvv: true,
      },
      errorMsg: '',
    };
  },
  computed: {
    cardTypeInfoFormatted() {
      // 处理卡类型并进行去重
      const seen = new Set();
      return (this.cardTypeInfo || []).map(item => {
        // 统一处理VISA和MASTERCARD
        let cardType = item.cardType;
        if (cardType.includes('VISA COMMERCIAL') && cardType.includes('VISA')) cardType = 'VISA';
        if (cardType.includes('MASTERCARD COMMERCIAL') && cardType.includes('MASTERCARD')) cardType = 'MASTERCARD';

        return { ...item, cardType };
      }).filter(item => {
        // 使用Set进行去重
        if (!seen.has(item.cardType)) {
          seen.add(item.cardType);
          return true;
        }
        return false;
      });
    },
    formCardNum: {
      get() {
        return this.cardNum;
      },
      set(newValue) {
        this.$emit('update:cardNum', newValue);
      },
    },
    formEffectiveDate: {
      get() {
        return this.effectiveDate;
      },
      set(newValue) {
        this.$emit('update:effectiveDate', newValue);
      },
    },
    formCvv: {

      get() {
        return this.cvv;
      },
      set(newValue) {
        this.$emit('update:cvv', newValue);
      },
    },

    /**
     * 配置了必填且值为空才走必填校验
     *
     * 这里先确认配置是否有，再确认当前是否要校验（目前的逻辑是，不点pay按钮就不走必填校验）
     */
    // 卡号必填
    cardNumRequired() {
      return this.rules.cardNum?.some(item => item.name === 'required' && item.required) && !this.formCardNum;
    },

    // 有效期必填
    effectiveDateRequired() {
      return (this.rules.effectiveDate?.some(item => item.name === 'required' && item.required) && !this.formEffectiveDate);
    },

    // cvv必填
    cvvRequired() {
      return (this.rules.cvv?.some(item => item.name === 'required' && !!item.required) && !this.formCvv);
    },
  },
  mounted() {
    this.startCarousel();
  },
  methods: {
    cardNumberRules,
    effectiveDateRules,
    cvvRules,
    startCarousel() {
      setInterval(() => {
        this.activeIndex = (this.activeIndex + 1) % this.cardTypeInfoFormatted.slice(3).length;
      }, 2000);
    },

    cardNumberBlur() {
      this.validate();
      this.$emit('cardNumberBlur', this.formCardNum);
    },

    effectiveDateBlur() {
      this.validate();
      this.$emit('effectiveDateBlur', this.formEffectiveDate);
    },

    cvvBlur() {
      this.validate();
      this.$emit('cvvBlur', this.formCvv);
    },

    cardNumberFocus() {
      this.$emit('inputFocus', {
        name: 'cardNum',
        value: this.formCardNum,
      });
    },

    effectiveDateFocus() {
      this.$emit('inputFocus', {
        name: 'effectiveDate',
        value: this.formEffectiveDate,
      });
    },

    cvvFocus() {
      this.$emit('inputFocus', {
        name: 'cvv',
        value: this.formCvv,
      });
    },

    cardNumberChange() {
      this.$emit('inputChange', {
        name: 'cardNum',
        value: this.formCardNum,
      });
    },

    effectiveDateChange() {
      this.$emit('inputChange', {
        name: 'effectiveDate',
        value: this.formEffectiveDate,
      });
    },

    cvvChange() {
      this.$emit('inputChange', {
        name: 'cvv',
        value: this.formCvv,
      });
    },

    // 校验方法
    validate() {
      // 按照优先级，先校验卡号，再校验有效期，再校验cvv 返回错误信息
      const cardNumRes = this.cardNumberRules(this.formCardNum);
      const effectiveDateRes = this.effectiveDateRules(this.formEffectiveDate);
      const cvvRes = this.cvvRules(this.formCvv);

      this.validator.cardNum = cardNumRes.result;
      this.validator.effectiveDate = effectiveDateRes.result;
      this.validator.cvv = cvvRes.result;

      this.errorMsg = cardNumRes.message || effectiveDateRes.message || cvvRes.message;
    },
    // 获取校验结果
    getValidateResult() {
      return !(this.cardNumRequired || !this.validator.cardNum) &&
        !(this.effectiveDateRequired || !this.validator.effectiveDate) &&
        !(this.cvvRequired || !this.validator.cvv);
    },
    // 卡号输入时动态展示银行卡图片
    changeHandler(evt) {
      const selectionStart = evt.target.selectionStart;
      let formattedNumber = this.formCardNum.replace(/\D/g, '');
      formattedNumber = formattedNumber.replace(/(.{4})/g, '$1 ').trim();
      this.formCardNum = formattedNumber;
      this.$emit('update:value', this.formCardNum);
      this.card = getCard(this.formCardNum);
      if (!this.card) {
        this.card = { logoPath: require('@/assets/images/cards/Default.png') };
      }
      this.$nextTick(() => {
        let s = 0;
        if (evt.data === null && selectionStart % 5 === 0) { // 删除光标位置不变
          s = 0;
        } else if (evt.data !== null && !/^\d$/.test(evt.data)) { // 输入非数字光标位置-1
          s = -1;
        } else if (selectionStart % 5 === 0) { // 碰到空格光标位置+1
          s = 1;
        }
        evt.target.selectionStart = evt.target.selectionEnd = selectionStart + s;
      });
    },
    // 动态展示银行卡图片
    showCardImage() {
      let formattedNumber = this.formCardNum.replace(/\D/g, '');
      formattedNumber = formattedNumber.replace(/(.{4})/g, '$1 ').trim();
      this.formCardNum = formattedNumber;
      this.$emit('update:value', this.formCardNum);
      this.card = getCard(this.formCardNum);
      if (!this.card) {
        this.card = { logoPath: require('@/assets/images/cards/Default.png') };
      }
    },
    // effectiveDateChangeHandler(evt) {
    //   if (evt.inputType === 'deleteContentBackward') {
    //     return;
    //   }
    //   this.formEffectiveDate = this.formEffectiveDate.replace(/[^\d/]/g, '');
    //   // 想要使用/分隔，分隔符号前后加空格
    //   this.formEffectiveDate = this.formEffectiveDate.replace(/(.{2})/g, '$1 ');
    // },
    effectiveDateChangeHandler(evt) {
      if (evt.inputType === 'deleteContentBackward') {
        return;
      }
      let value = event.target.value;
      this.formEffectiveDate = value.replace(/[^\d/]/g, '');
      // 自动转化 00 为 01
      if (value.length === 2 && parseInt(value, 10) === 0) {
        value = '01';
      }

      // 自动添加斜杠 "/"
      if (value.length === 2 && !value.includes('/')) {
        value += ' / ';
      }

      // 确保斜杠前后有空格
      if (value.includes('/')) {
        value = value.replace(/\s*\/\s*/, ' / ');
      }
      // 更新输入框值
      this.formEffectiveDate = value;
    },
    cvvChangeHandler(event) {
      // this.formCvv = this.formCvv.replace(/[^\d]/g, '');
      const value = event.target.value.replace(/[^\d]/g, '');
      this.formCvv = value;
    },
  },

  watch: {
    // formCardNum(newVal) {
    //   this.showCardImage(newVal);
    // },
    formCardNum: {
      handler(newVal) {
        this.showCardImage(newVal);
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
$color-primary: #503DF6;

.card-info-suffix {
  // width: 132px;
  // margin-right: 12px;

  .card-info-suffix-content {
    display: flex;
    align-items: center;
    justify-content: end;
    gap: 4px;

    img {
      width: 32px;
      height: auto;
    }
  }
}

.card-info-suffix-card {
  // margin-right: 12px;
  display: flex;
  align-items: center;
}

.img-cvv-box {
  display: flex;
  height: 100%;
  align-items: center;
  // margin-right: 12px;
}

.icon-cvv {
  width: 32px;
  height: 25px;
  transform: translateY(-3px);
}

.fixed-item {
  width: 32px;
  height: auto;
}

.fade-carousel {
  position: relative;
  width: 32px;
  height: auto;
  bottom: 11px;
}

.fade-item {
  position: absolute;
  top: 0;
  left: 0;
  width: 30px;
  height: 16px;
  opacity: 0;
  transition: opacity 1s ease;
}

.fade-item.active {
  opacity: 1;
}

.card-info-under-group {
  display: flex;
  align-items: center;
  margin-top: -2px;
  position: relative;
  height: auto;
}

.card-info-input {
  height: auto;
  display: flex;
  ::v-deep .el-input__inner {
    border-radius: 8px 8px 0 0;

    &:focus {
      z-index: 10;
    }
  }

}

.effectiveDate-input {
  ::v-deep .el-input__inner {
    border-radius: 0px 0px 0 8px;

    &:focus {
      z-index: 10;
    }
  }
}

.cvv-input {
  ::v-deep .el-input__inner {
    border-radius: 0px 0px 8px 0;
    margin-left: -1px;
    width: calc(100% + 1px);

    &:focus {
      z-index: 10;
    }
  }
}

.is-invalid {
  ::v-deep .el-input__inner {
    border-color: #F53F3F;
    z-index: 9;
  }
}

.error-msg {
  color: #F53F3F;
  font-size: 14px;
  line-height: 18px;
  margin-top: 6px;
  font-family: "Open Sans regular";
}

:deep(.el-input__suffix) {
  right: 12px !important;
}
</style>
